import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Chip,
  TextField,
  Button,
  Grid,
  Typography,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
} from "@mui/material";
import { Clear, FilterList } from "@mui/icons-material";

const TechnicianFilters = ({
  onFilterChange,
  onDateFilter,
  clientsList = [],
  centersList = [],
}) => {
  const [filters, setFilters] = useState({
    technician_obj_id: "",
    primary_services: "",
    secondary_services: "",
    pincode: "",
    is_active: "",
    center_id: "",
    phone_number: "",
    engagement_type: "",
    service_area_main_pincode: "",
    city: "",
    is_otp_verfied: "",
  });

  const [dateRange, setDateRange] = useState({
    start_date: "",
    end_date: "",
  });

  const [activeFilters, setActiveFilters] = useState([]);

  // Load filters from localStorage on component mount
  useEffect(() => {
    const savedFilters = localStorage.getItem("technicianFilters");
    const savedDateRange = localStorage.getItem("technicianDateRange");
    const savedActiveFilters = localStorage.getItem("technicianActiveFilters");

    if (savedFilters) {
      const parsedFilters = JSON.parse(savedFilters);
      setFilters(parsedFilters);
    }

    if (savedDateRange) {
      const parsedDateRange = JSON.parse(savedDateRange);
      setDateRange(parsedDateRange);
    }

    if (savedActiveFilters) {
      setActiveFilters(JSON.parse(savedActiveFilters));
    }
  }, []);

  // Update active filters whenever filters change
  useEffect(() => {
    const newActiveFilters = [];

    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== "") {
        newActiveFilters.push({
          key,
          label: getFilterLabel(key, value),
          value,
        });
      }
    });

    Object.entries(dateRange).forEach(([key, value]) => {
      if (value && value !== "") {
        newActiveFilters.push({
          key,
          label: getFilterLabel(key, value),
          value,
        });
      }
    });

    setActiveFilters(newActiveFilters);
    localStorage.setItem(
      "technicianActiveFilters",
      JSON.stringify(newActiveFilters)
    );
  }, [filters, dateRange]);

  const getFilterLabel = (key, value) => {
    const labelMap = {
      technician_obj_id: "Technician ID",
      primary_services: "Primary Services",
      secondary_services: "Secondary Services",
      pincode: "Pincode",
      is_active: "Status",
      center_id: "Center",
      phone_number: "Phone Number",
      engagement_type: "Engagement Type",
      service_area_main_pincode: "Service Pincode",
      city: "City",
      is_otp_verfied: "OTP Verified",
      start_date: "Start Date",
      end_date: "End Date",
    };

    if (key === "is_active") {
      return `Status: ${value === "true" ? "Active" : "Inactive"}`;
    }
    if (key === "is_otp_verfied") {
      return `OTP Verified: ${value === "true" ? "Yes" : "No"}`;
    }
    if (key === "engagement_type") {
      const typeMap = {
        FULL_TIME: "Full Time",
        PART_TIME: "Part Time",
        CONTRACT: "Contract",
      };
      return `Engagement: ${typeMap[value] || value}`;
    }

    return `${labelMap[key]}: ${value}`;
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    localStorage.setItem("technicianFilters", JSON.stringify(newFilters));
  };

  const handleDateChange = (key, value) => {
    const newDateRange = { ...dateRange, [key]: value };
    setDateRange(newDateRange);
    localStorage.setItem("technicianDateRange", JSON.stringify(newDateRange));
  };

  const handleApplyFilters = () => {
    const allFilters = { ...filters, ...dateRange };

    // Call only one function that handles both regular filters and date filters
    onFilterChange(allFilters);
  };

  const handleClearFilters = () => {
    const emptyFilters = {
      technician_obj_id: "",
      primary_services: "",
      secondary_services: "",
      pincode: "",
      is_active: "",
      center_id: "",
      phone_number: "",
      engagement_type: "",
      service_area_main_pincode: "",
      city: "",
      is_otp_verfied: "",
    };

    const emptyDateRange = {
      start_date: "",
      end_date: "",
    };

    setFilters(emptyFilters);
    setDateRange(emptyDateRange);
    setActiveFilters([]);

    localStorage.removeItem("technicianFilters");
    localStorage.removeItem("technicianDateRange");
    localStorage.removeItem("technicianActiveFilters");

    onFilterChange(emptyFilters);
    onDateFilter(["", ""]);
  };

  const removeFilter = (filterKey) => {
    if (filterKey === "start_date" || filterKey === "end_date") {
      const newDateRange = { ...dateRange, [filterKey]: "" };
      setDateRange(newDateRange);
      localStorage.setItem("technicianDateRange", JSON.stringify(newDateRange));

      const allFilters = { ...filters, ...newDateRange };
      onFilterChange(allFilters);
    } else {
      const newFilters = { ...filters, [filterKey]: "" };
      setFilters(newFilters);
      localStorage.setItem("technicianFilters", JSON.stringify(newFilters));
      onFilterChange(newFilters);
    }
  };

  return (
    <Paper className="p-6 mb-6 shadow-lg rounded-lg border border-gray-200">
      {/* Active Filters */}
      {activeFilters.length > 0 && (
        <Box className="mb-4">
          <Typography variant="subtitle2" className="text-gray-600 mb-2">
            Active Filters:
          </Typography>
          <Box className="flex flex-wrap gap-2">
            {activeFilters.map((filter) => (
              <Chip
                key={filter.key}
                label={filter.label}
                onDelete={() => removeFilter(filter.key)}
                deleteIcon={<Clear />}
                variant="outlined"
                color="primary"
                className="border-blue-500 text-blue-600"
              />
            ))}
            <Button
              size="small"
              onClick={handleClearFilters}
              className="text-red-600 hover:text-red-800"
            >
              Clear All
            </Button>
          </Box>
        </Box>
      )}

      {/* Filter Form */}
      <Grid container spacing={3} alignItems="flex-end">
        {/* Date Range */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            type="date"
            label="Start Date"
            value={dateRange.start_date}
            onChange={(e) => handleDateChange("start_date", e.target.value)}
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            size="small"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            type="date"
            label="End Date"
            value={dateRange.end_date}
            onChange={(e) => handleDateChange("end_date", e.target.value)}
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            size="small"
          />
        </Grid>

        {/* Technician ID */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="Technician ID"
            value={filters.technician_obj_id}
            onChange={(e) =>
              handleFilterChange("technician_obj_id", e.target.value)
            }
            variant="outlined"
            size="small"
          />
        </Grid>

        {/* Phone Number */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="Phone Number"
            value={filters.phone_number}
            onChange={(e) => handleFilterChange("phone_number", e.target.value)}
            variant="outlined"
            size="small"
          />
        </Grid>

        {/* Pincode */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="Pincode"
            value={filters.pincode}
            onChange={(e) => handleFilterChange("pincode", e.target.value)}
            variant="outlined"
            size="small"
          />
        </Grid>

        {/* Service Pincode */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="Service Pincode"
            value={filters.service_area_main_pincode}
            onChange={(e) =>
              handleFilterChange("service_area_main_pincode", e.target.value)
            }
            variant="outlined"
            size="small"
          />
        </Grid>

        {/* City */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="City"
            value={filters.city}
            onChange={(e) => handleFilterChange("city", e.target.value)}
            variant="outlined"
            size="small"
          />
        </Grid>

        {/* Status */}
        <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 220 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.is_active}
              onChange={(e) => handleFilterChange("is_active", e.target.value)}
              label="Status"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* OTP Verified */}
        {/* <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 220 }}>
          <FormControl fullWidth size="small">
            <InputLabel>OTP Verified</InputLabel>
            <Select
              value={filters.is_otp_verfied}
              onChange={(e) =>
                handleFilterChange("is_otp_verfied", e.target.value)
              }
              label="OTP Verified"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>
        </Grid> */}

        {/* Engagement Type */}
        {/* <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 220 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Engagement Type</InputLabel>
            <Select
              value={filters.engagement_type}
              onChange={(e) =>
                handleFilterChange("engagement_type", e.target.value)
              }
              label="Engagement Type"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="FULL_TIME">Full Time</MenuItem>
              <MenuItem value="PART_TIME">Part Time</MenuItem>
              <MenuItem value="CONTRACT">Contract</MenuItem>
            </Select>
          </FormControl>
        </Grid> */}

        {/* Action Buttons */}
        <Grid item xs={12} className="flex justify-end space-x-2">
          <Button
            variant="outlined"
            onClick={handleClearFilters}
            className="border-gray-300 text-gray-600 hover:border-gray-400"
          >
            Clear
          </Button>
          <Button
            variant="contained"
            onClick={handleApplyFilters}
            startIcon={<FilterList />}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Apply Filters
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default TechnicianFilters;
